import { parseApiDate } from "@/lib/dates";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useMyInvitations } from "@/hooks/useMyInvitations";
import { getApiErrorMessage } from "@/stores/authStore";
import type { GroupInvitation } from "@/types";

interface MyInvitesDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

function formatDate(iso: string) {
  return parseApiDate(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntilExpiry(iso: string): number {
  return Math.max(0, Math.ceil((parseApiDate(iso).getTime() - Date.now()) / 86_400_000));
}

export function MyInvitesDialog({ open, onOpenChange }: MyInvitesDialogProps) {
  const { invitations, isLoading, error, refetch, accept } = useMyInvitations();
  const [accepting, setAccepting] = useState<string | null>(null);

  const handleAccept = async (invitation: GroupInvitation) => {
    setAccepting(invitation.uuid);
    try {
      await accept(invitation);
      toast.success(`You've joined ${invitation.group?.name ?? "the group"}!`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setAccepting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Pending Invitations</DialogTitle>
          <DialogDescription>
            Groups you've been invited to join.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {isLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
          )}

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {!isLoading && !error && invitations.length === 0 && (
            <div className="py-10 text-center">
              <CheckCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No pending invitations</p>
            </div>
          )}

          {invitations.map((inv) => {
            const days = daysUntilExpiry(inv.expiresAt);
            const inviterName = inv.inviter
              ? `${inv.inviter.firstName} ${inv.inviter.lastName}`
              : "Someone";

            return (
              <div
                key={inv.uuid}
                className="flex items-start justify-between gap-3 rounded-lg border border-border p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10">
                    <Users className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold">{inv.group?.name ?? "Unknown Group"}</p>
                    <p className="text-sm text-muted-foreground">
                      Invited by {inviterName} &middot; {formatDate(inv.createdAt)}
                    </p>
                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      Expires in {days} day{days !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="accent"
                  onClick={() => handleAccept(inv)}
                  disabled={accepting === inv.uuid}
                  className="shrink-0"
                >
                  {accepting === inv.uuid ? "Joining..." : "Accept"}
                </Button>
              </div>
            );
          })}
        </div>

        {!isLoading && invitations.length > 0 && (
          <p className="text-center text-xs text-muted-foreground">
            {invitations.length} pending invitation{invitations.length !== 1 ? "s" : ""}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
